import { useEffect, useState, useCallback } from "react";
import { ShieldCheck, Users, TrendingUp, Calendar, LogOut } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { adminSignups, verifyAdmin, setAdminKey, hasAdminKey } from "../lib/api";

function AdminGate({ onUnlock }) {
  const [key, setKey] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await verifyAdmin(key);
      setAdminKey(key);
      onUnlock();
    } catch {
      setErr("Invalid admin key.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen bg-[#FAF9F5]">
      <Navbar />
      <div className="mx-auto flex max-w-md flex-col items-center px-4 pb-24 pt-28 text-center">
        <div className="rounded-full border border-[#e8e6df] bg-white p-4 shadow-sm">
          <ShieldCheck className="h-8 w-8 text-[#157a59]" />
        </div>
        <h1 className="mt-5 font-heading text-3xl font-bold text-[#1a1a18]">Admin dashboard</h1>
        <p className="mt-2 text-sm text-neutral-500">Sign-ups, users and growth metrics — restricted to administrators.</p>
        <form onSubmit={submit} className="mt-6 w-full space-y-3">
          <input type="password" value={key} onChange={(e) => setKey(e.target.value)}
            placeholder="Admin key" data-testid="admin-key-input" autoFocus
            className="w-full rounded-md border border-[#e8e6df] bg-white px-4 py-2.5 text-sm outline-none focus:border-[#157a59]" />
          {err && <p data-testid="admin-key-error" className="text-sm text-red-600">{err}</p>}
          <button type="submit" disabled={busy || !key} data-testid="admin-key-submit"
            className="w-full rounded-md bg-[#1a1a18] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#157a59] disabled:opacity-50">
            {busy ? "Checking…" : "Unlock"}
          </button>
        </form>
      </div>
      <Footer />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-[#e8e6df] bg-white p-5">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-neutral-500">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="mt-2 font-heading text-4xl font-bold text-[#1a1a18]">{value}</div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
      " · " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  } catch { return iso.slice(0, 19); }
}

function TierBadge({ tier }) {
  const map = {
    pro: "bg-[#1a1a18] text-white",
    institutional: "bg-[#7F3AAD] text-white",
    free: "bg-neutral-100 text-neutral-600",
  };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${map[tier] || map.free}`}>
      {tier}
    </span>
  );
}

function Dashboard({ onLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      setData(await adminSignups());
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load sign-ups.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[#FAF9F5]">
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#157a59]">
              <ShieldCheck className="h-4 w-4" /> Admin
            </div>
            <h1 className="mt-2 font-heading text-4xl font-bold tracking-tight text-[#1a1a18] md:text-5xl">
              Sign-ups &amp; users
            </h1>
            <p className="mt-2 max-w-2xl text-neutral-600">
              Everyone who has signed in via Google — with growth, tier mix and the recent list.
            </p>
          </div>
          <button onClick={onLogout} data-testid="admin-logout"
            className="inline-flex items-center gap-1.5 rounded-full border border-[#e8e6df] bg-white px-4 py-2 text-xs font-medium text-neutral-600 transition-colors hover:border-neutral-400">
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>

        {loading && <div className="mt-12 text-sm text-neutral-400">Loading sign-ups…</div>}
        {err && <div className="mt-8 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>}

        {data && (
          <>
            <div data-testid="admin-stat-grid" className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <StatCard testid="stat-total" icon={Users} label="Total sign-ups" value={data.total} />
              <StatCard testid="stat-today" icon={Calendar} label="Today" value={data.today} />
              <StatCard testid="stat-week" icon={TrendingUp} label="Last 7 days" value={data.this_week} />
              <StatCard testid="stat-pro" icon={Users} label="Paid (Pro + Inst.)"
                value={(data.tiers?.pro || 0) + (data.tiers?.institutional || 0)} />
            </div>

            <div className="mt-10 rounded-lg border border-[#e8e6df] bg-white p-6">
              <h2 className="font-heading text-lg font-semibold text-[#1a1a18]">Daily sign-ups · last 30 days</h2>
              <div className="mt-4 h-72 w-full" data-testid="admin-signup-chart">
                <ResponsiveContainer>
                  <LineChart data={data.daily} margin={{ top: 8, right: 16, left: -20, bottom: 0 }}>
                    <CartesianGrid stroke="#efece2" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#8a887f" }} tickLine={false}
                      tickFormatter={(d) => d.slice(5)} axisLine={{ stroke: "#e8e6df" }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#8a887f" }} tickLine={false}
                      axisLine={{ stroke: "#e8e6df" }} />
                    <Tooltip
                      contentStyle={{ fontSize: 12, border: "1px solid #e8e6df", borderRadius: 6 }}
                      labelFormatter={(d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      formatter={(v) => [v, "Sign-ups"]} />
                    <Line type="monotone" dataKey="signups" stroke="#E24B4A" strokeWidth={2}
                      dot={{ r: 3, fill: "#E24B4A", stroke: "#E24B4A" }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="mt-10">
              <h2 className="font-heading text-lg font-semibold text-[#1a1a18]">
                Recent users {data.total > 50 && <span className="text-sm font-normal text-neutral-400">(showing 50 of {data.total})</span>}
              </h2>
              <div className="mt-3 overflow-hidden rounded-lg border border-[#e8e6df] bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-[#f5f4ef] text-xs uppercase tracking-wider text-neutral-500">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Signed up</th>
                      <th className="px-4 py-3 text-left font-semibold">Name</th>
                      <th className="px-4 py-3 text-left font-semibold">Email</th>
                      <th className="px-4 py-3 text-left font-semibold">Tier</th>
                    </tr>
                  </thead>
                  <tbody data-testid="admin-user-table">
                    {data.recent.length === 0 && (
                      <tr><td colSpan={4} className="px-4 py-10 text-center text-neutral-400">No users yet.</td></tr>
                    )}
                    {data.recent.map((u) => (
                      <tr key={u.email} data-testid={`admin-user-${u.email}`} className="border-t border-[#f0eee6]">
                        <td className="whitespace-nowrap px-4 py-3 text-neutral-500">{fmtDate(u.created_at)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {u.picture ? (
                              <img src={u.picture} alt="" referrerPolicy="no-referrer" className="h-6 w-6 rounded-full" />
                            ) : (
                              <span className="grid h-6 w-6 place-items-center rounded-full bg-[#1a1a18] text-[10px] font-semibold text-white">
                                {(u.name || u.email || "?").charAt(0).toUpperCase()}
                              </span>
                            )}
                            <span className="text-neutral-800">{u.name || "—"}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-neutral-600">{u.email}</td>
                        <td className="px-4 py-3"><TierBadge tier={u.subscription_tier} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default function Admin() {
  const [unlocked, setUnlocked] = useState(hasAdminKey());
  const logout = () => {
    sessionStorage.removeItem("cor_admin_key");
    setUnlocked(false);
  };
  if (!unlocked) return <AdminGate onUnlock={() => setUnlocked(true)} />;
  return <Dashboard onLogout={logout} />;
}
